import { z } from 'zod';

export const ProviderModeSchema = z.enum(['live', 'demo']);
export type ProviderMode = z.infer<typeof ProviderModeSchema>;

export const ProviderStatusSchema = z.object({
  speech: ProviderModeSchema,
  tutor: ProviderModeSchema,
  storage: ProviderModeSchema,
  realtime: ProviderModeSchema,
});
export type ProviderStatus = z.infer<typeof ProviderStatusSchema>;

export const SkillProgressSchema = z.object({
  id: z.enum(['pronunciation', 'fluency', 'vocabulary', 'grammar']),
  labelZh: z.string(),
  labelEn: z.string(),
  value: z.number().min(0).max(100),
});

export const BootstrapSchema = z.object({
  placement: z.object({ required: z.boolean(), status: z.enum(['not_started', 'in_progress', 'pending_review', 'reviewed']), level: z.enum(['beginner', 'intermediate', 'advanced']).nullable(), attemptId: z.string().uuid().nullable() }).optional(),
  learner: z.object({
    nameZh: z.string(),
    nameEn: z.string(),
    level: z.string(),
    levelLabel: z.string(),
    streak: z.number(),
    xp: z.number(),
    coins: z.number(),
  }),
  skills: z.array(SkillProgressSchema),
  providerStatus: ProviderStatusSchema,
  /** Microphone capture tuning, so a quiet mic can be compensated at runtime. */
  audio: z.object({
    inputGain: z.number().min(0).max(8),
    noiseSuppression: z.boolean(),
  }),
});
export type Bootstrap = z.infer<typeof BootstrapSchema>;

export const AssessmentStatusSchema = z.enum(['scored', 'heuristic', 'insufficient', 'unavailable', 'error']);
export type AssessmentStatus = z.infer<typeof AssessmentStatusSchema>;

const NullableScore = z.number().nullable();

export const AlignmentSyllableSchema = z.object({
  index: z.number().int(),
  text: z.string(),
  jyutping: z.string(),
  tone: z.number().int(),
  start_s: z.number(),
  end_s: z.number(),
});
export type AlignmentSyllable = z.infer<typeof AlignmentSyllableSchema>;

export const AlignmentSchema = z.object({
  schema_version: z.string().optional(),
  alignment_method: z.string(),
  confidence: z.number(),
  audio_duration_s: z.number(),
  syllables: z.array(AlignmentSyllableSchema),
});
export type Alignment = z.infer<typeof AlignmentSchema>;

export const InitialFinalScoreSchema = z.object({
  initial: z.object({
    symbol: z.string(),
    accuracy: NullableScore,
    error: z.string().nullable().optional(),
    feedback_zh: z.string().nullable().optional(),
    feedback_en: z.string().nullable().optional(),
  }).optional(),
  final: z.object({
    symbol: z.string(),
    accuracy: NullableScore,
    error: z.string().nullable().optional(),
    feedback_zh: z.string().nullable().optional(),
    feedback_en: z.string().nullable().optional(),
  }).optional(),
}).optional();
export type InitialFinalScore = z.infer<typeof InitialFinalScoreSchema>;

export const SegmentalSyllableSchema = z.object({
  index: z.number().int(),
  text: z.string().nullable().optional(),
  jyutping: z.string().nullable().optional(),
  accuracy: NullableScore.optional(),
  error_type: z.string().nullable().optional(),
  heard: z.string().nullable().optional(),
  duration_s: z.number().nullable().optional(),
  duration_deviation: z.number().nullable().optional(),
  // Azure phoneme-level I/F scores (flat)
  initial: z.string().nullable().optional(),
  final: z.string().nullable().optional(),
  initial_accuracy: NullableScore.optional(),
  final_accuracy: NullableScore.optional(),
  // Fused provider scores (Azure word score + ElevenLabs recognition)
  azure_accuracy: NullableScore.optional(),
  elevenlabs_accuracy: NullableScore.optional(),
  elevenlabs_probability: NullableScore.optional(),
  if_fallback: z.boolean().optional(),
  if_source: z.string().optional(),
  // Legacy nested I/F (from jyutpingParser)
  initial_final: InitialFinalScoreSchema,
}).passthrough();
export type SegmentalSyllable = z.infer<typeof SegmentalSyllableSchema>;

export const SegmentalSchema = z.object({
  provider: z.string(),
  status: AssessmentStatusSchema,
  accuracy: NullableScore,
  fluency: NullableScore,
  completeness: NullableScore,
  composite_score: NullableScore,
  transcript: z.string(),
  per_syllable: z.array(SegmentalSyllableSchema),
  details: z.object({
    recognition_status: z.string().optional(),
    language_mismatch: z.boolean().optional(),
    initial_final: z.object({
      initial_avg: z.number().optional(),
      initial_count: z.number().int().optional(),
      final_avg: z.number().optional(),
      final_count: z.number().int().optional(),
    }).optional(),
  }).passthrough(),
});
export type Segmental = z.infer<typeof SegmentalSchema>;

export const ToneSyllableSchema = z.object({
  index: z.number().int(),
  text: z.string(),
  jyutping: z.string(),
  expected_tone: z.number().int(),
  predicted_tone: z.number().int().nullable(),
  score: NullableScore,
  tier: z.string(),
  confidence_gap: NullableScore,
});
export type ToneSyllable = z.infer<typeof ToneSyllableSchema>;

export const ToneSchema = z.object({
  status: AssessmentStatusSchema,
  aggregate_score: NullableScore,
  reference_f0_hz: NullableScore,
  syllables: z.array(ToneSyllableSchema),
});
export type Tone = z.infer<typeof ToneSchema>;

export const ProsodySchema = z.object({
  status: AssessmentStatusSchema,
  rate_syllables_per_s: NullableScore,
  internal_pause_count: z.number().int().nullable(),
  pause_ratio: NullableScore,
  npvi: NullableScore,
  intonation_slope_st_per_s: NullableScore,
  f0_range_st: NullableScore,
  sub_scores: z.object({
    rate: z.number(),
    pauses: z.number(),
    intonation: z.number(),
    f0_range: z.number(),
  }).passthrough(),
  composite_score: NullableScore,
});
export type Prosody = z.infer<typeof ProsodySchema>;

export const CompositeSchema = z.object({
  score: NullableScore,
  weights: z.record(z.number()),
  basis: z.array(z.string()),
});
export type Composite = z.infer<typeof CompositeSchema>;

export const AssessmentMetaSchema = z.object({
  audio: z.string(),
  jyutping: z.string(),
  text: z.string(),
  pitch_backend: z.string(),
  alignment_method: z.string(),
  tone_reference: z.string().optional(),
  tone_reference_generator: z.string().optional(),
}).passthrough();
export type AssessmentMeta = z.infer<typeof AssessmentMetaSchema>;

export const AssessmentResultSchema = z.object({
  schema_version: z.string(),
  engine_version: z.string(),
  meta: AssessmentMetaSchema,
  alignment: AlignmentSchema,
  segmental: SegmentalSchema,
  tone: ToneSchema,
  prosody: ProsodySchema,
  composite: CompositeSchema,
}).passthrough();
export type AssessmentResult = z.infer<typeof AssessmentResultSchema>;

export const AssessmentResponseSchema = z.object({
  mode: ProviderModeSchema,
  result: AssessmentResultSchema,
});
export type AssessmentResponse = z.infer<typeof AssessmentResponseSchema>;

export const TranslationFocusSchema = z.object({
  cantonese: z.string(),
  jyutping: z.string(),
  partOfSpeechZh: z.string(),
  partOfSpeechEn: z.string(),
  tone: z.number().int().min(1).max(6),
  tipZh: z.string(),
  tipEn: z.string(),
});

export const TranslationVocabularySchema = z.object({
  cantonese: z.string(),
  jyutping: z.string(),
  english: z.string(),
  noteZh: z.string(),
  noteEn: z.string(),
});

export const TranslationGrammarSchema = z.object({
  titleZh: z.string(),
  titleEn: z.string(),
  explanationZh: z.string(),
  explanationEn: z.string(),
});

export const TranslationAnswerSchema = z.object({
  /** Auto-detected source language of the phrase to translate. */
  sourceLanguage: z.enum(['english', 'mandarin']),
  sourceText: z.string(),
  /** Colloquial spoken Hong Kong Cantonese (Traditional characters). */
  translationZh: z.string(),
  /** LSHK Jyutping, one syllable per token, tone numbers 1-6. */
  translationJyutping: z.string(),
  translationEn: z.string(),
  exampleZh: z.string(),
  exampleJyutping: z.string(),
  exampleEn: z.string(),
  focus: TranslationFocusSchema,
  vocabulary: z.array(TranslationVocabularySchema).max(6),
  grammar: z.array(TranslationGrammarSchema).max(3),
  related: z.array(z.object({ cantonese: z.string(), jyutping: z.string(), english: z.string() })).max(4),
  mode: ProviderModeSchema,
});
export type TranslationAnswer = z.infer<typeof TranslationAnswerSchema>;

export const ChatTurnSchema = z.object({
  id: z.string(),
  /** Template-defined role key (`template.roles.ai.key` / `.learner.key`). */
  role: z.string(),
  cantonese: z.string(),
  jyutping: z.string().optional(),
  english: z.string().optional(),
});
export type ChatTurn = z.infer<typeof ChatTurnSchema>;

export const ConversationReportSchema = z.object({
  pronunciation: z.number().min(0).max(100),
  vocabulary: z.number().min(0).max(100),
  grammar: z.number().min(0).max(100),
  summaryZh: z.string(),
  summaryEn: z.string(),
  recommendedZh: z.string(),
  recommendedEn: z.string(),
  mode: ProviderModeSchema,
});
export type ConversationReport = z.infer<typeof ConversationReportSchema>;

// Scenario shapes live in `scenarioTemplate.ts` so the server (full template)
// and the browser (client projection) share one definition. Re-exported here
// under the legacy names to keep existing imports working.
import type { ClientScenario, ScenarioObjectiveStep } from './scenarioTemplate.js';

export type ChatScenario = ClientScenario;
export type ScenarioStep = ScenarioObjectiveStep;
export type { ClientScenario, ScenarioObjectiveStep };
export {
  ClientScenarioSchema as ChatScenarioSchema,
  ScenarioObjectiveStepSchema as ScenarioStepSchema,
} from './scenarioTemplate.js';

export const ChatTranscriptEntrySchema = z.object({
  /** Template-defined role key (`template.roles.ai.key` / `.learner.key`). */
  role: z.string(),
  text: z.string(),
});
export type ChatTranscriptEntry = z.infer<typeof ChatTranscriptEntrySchema>;

export const ChatFailReasonSchema = z.enum(['gave_up', 'could_not_progress', 'off_task', 'time_up', 'silence', 'turn_limit', 'max_attempts_exceeded']);
export type ChatFailReason = z.infer<typeof ChatFailReasonSchema>;

export const ConversationFeedbackItemSchema = z.object({
  turnIndex: z.number(),
  utterance: z.string(),
  issueType: z.enum(['inauthentic_phrase', 'mandarin_influence', 'unnatural_expression', 'non_cantonese', 'asr_error']),
  descriptionZh: z.string(),
  descriptionEn: z.string(),
  suggestedZh: z.string().optional(),
  /** False for provider transcription errors that should not reduce the learner's score. */
  affectsScore: z.boolean().optional(),
});
export type ConversationFeedbackItem = z.infer<typeof ConversationFeedbackItemSchema>;

export const ConversationAssessmentSchema = z.object({
  finalScore: z.number(),
  styleDeduction: z.number(),
  understandabilityDeduction: z.number(),
  nonCantoneseTurns: z.number(),
  totalTurns: z.number(),
  feedback: z.array(ConversationFeedbackItemSchema),
});
export type ConversationAssessment = z.infer<typeof ConversationAssessmentSchema>;

export const ChatResultSchema = z.object({
  outcome: z.enum(['pass', 'fail']),
  reason: ChatFailReasonSchema.optional(),
  summary: z.string(),
  completedSteps: z.array(z.string()),
  transcript: z.array(ChatTranscriptEntrySchema),
  recordingId: z.string().nullable(),
  mode: ProviderModeSchema,
  /** Only present when outcome='pass' and all objectives completed */
  assessment: ConversationAssessmentSchema.optional(),
});
export type ChatResult = z.infer<typeof ChatResultSchema>;

export type RealtimeChatStatusState =
  | 'listening'
  | 'learner-speaking'
  | 'learner-stopped'
  | 'waiter-speaking'
  | 'waiter-done'
  | 'tool-call-started'
  | 'tool-call-completed'
  | 'closing';

export type RealtimeChatServerMessage =
  | { type: 'ready'; scenario: ChatScenario }
  | { type: 'status'; state: RealtimeChatStatusState; tool?: string }
  | { type: 'transcript'; role: 'waiter' | 'learner'; text: string }
  | { type: 'objective_update'; completedSteps: string[]; currentStep: string | null }
  | { type: 'all_objectives_complete' }
  | { type: 'assessing_conversation' }
  | { type: 'assessment_complete'; assessment: ConversationAssessment }
  | { type: 'result'; result: ChatResult }
  | { type: 'error'; message: string }
  | { type: 'language_warning'; messageZh: string; messageEn: string; confidence: number };

export const ApiErrorSchema = z.object({ error: z.string() });
